import React, { useEffect, useRef } from 'react';

const DEFAULT_COLORS = {
  bg: '#0d1117',
  card: '#161b22',
  border: '#30363d',
  text: '#e6edf3',
  textMuted: '#8b949e',
  danger: '#f85149',
  accent: '#58a6ff'
};

/**
 * Google SSO login screen component.
 *
 * Props:
 *   - googleClientId: string (required) — Google OAuth client ID
 *   - onCredential: (credential: string) => void (required) — called with the Google ID token
 *   - appName: string (default: 'bstock Internal Tools')
 *   - subtitle: string (default: 'Sign in with your @bstock.com Google account')
 *   - error: string | null — error message to display
 *   - colors: object — color overrides (bg, card, border, text, textMuted, danger, accent)
 */
export function LoginScreen({
  googleClientId,
  onCredential,
  appName = 'bstock Internal Tools',
  subtitle = 'Sign in with your @bstock.com Google account',
  error = null,
  colors: colorOverrides = {}
}) {
  const buttonRef = useRef(null);
  const colors = { ...DEFAULT_COLORS, ...colorOverrides };

  useEffect(() => {
    const initGoogle = () => {
      if (!window.google || !buttonRef.current) return;

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => {
          onCredential(response.credential);
        }
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'filled_blue',
        size: 'large',
        width: 300,
        text: 'signin_with'
      });
    };

    if (window.google) {
      initGoogle();
    } else {
      const interval = setInterval(() => {
        if (window.google) {
          clearInterval(interval);
          initGoogle();
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [googleClientId, onCredential]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bg,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
    }}>
      <div style={{
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: '12px',
        padding: '48px',
        textAlign: 'center',
        maxWidth: '420px',
        width: '100%'
      }}>
        <h1 style={{
          color: colors.text,
          fontSize: '24px',
          fontWeight: 600,
          margin: '0 0 8px 0'
        }}>
          {appName}
        </h1>
        <p style={{
          color: colors.textMuted,
          fontSize: '14px',
          margin: '0 0 32px 0'
        }}>
          {subtitle}
        </p>
        <div
          ref={buttonRef}
          style={{ display: 'flex', justifyContent: 'center', minHeight: '44px' }}
        />
        {error && (
          <p style={{
            color: colors.danger,
            fontSize: '14px',
            marginTop: '16px'
          }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
